import * as React from 'react';
import './pdf.css';
import { observer, inject } from 'mobx-react';
import { BloodworkEntryStore } from '../stores/bloodwork-entry-store';
import { FurtherConsiderationsEntryStore } from '../stores/further-considerations-entry-store';
import { UserProfileUIStore } from '../stores/user-profile-ui-store';

import { Bloodwork } from '../models/Bloodwork';
import { Button, Card, Image } from 'react-bootstrap';

import { BloodworkAnalysis } from '../models/BloodworkAnalysis';
import { buildSystemGroupDefs, buildAttrToSystem, buildSecondaryTagger, appendSecondaryLabels, groupBySystem as groupMarkersBySystem } from '../models/mooSystemGrouping';
import { PanelField } from '../models/PanelField';
import Moment from 'react-moment';
import moment from 'moment';
import { FurtherConsiderations, Gender, Menopause, PanelMarker, Section } from '../models';
import { DisclaimerSection } from '../components/bloodworkz/disclaimer-section';
import { PDFSection } from '../components/bloodworkz/pdf-section';
import { SummaryDashboardSection } from '../components/bloodworkz/summary-dashboard-section';
import { ResultsOverviewPanel, hasResultsOverviewContent, isHdlHighSoleOutOfOptimal, isGlobulinOptimal, isImmuneAbsolute, keepInInflammationPanel, keepInImmunePanel, suppressAlpWhenZincSupersedes } from '../components/bloodworkz/results-overview-panel';
import { SupplementsSummaryPanel } from '../components/bloodworkz/supplements-summary-panel';
import {
  HolobiomeRoadmapPdf,
  PDF_PATTERN_TABLE_TITLE,
  estimatedHolobiomeRoadmapPdfHeight,
  isSystemicFormulasEnabled,
} from '../components/bloodworkz/supplement-protocols-panel';
import { HOLOBIOME_SECTION } from '../models/holobiomeKits';
import { supplementsVisible } from '../utils/supplementsVisibility';
import { SectionNotesSummaryPanel } from '../components/bloodworkz/section-notes-summary-panel';
import { renderNoteBody } from '../components/bloodworkz/note-markdown';
import { BMIBarChart } from '../components/charts/BMIBarChart';
import { MathUtils } from '../utils';
import { computePdfSectionPacking } from '../utils/pdfSectionPacking';
import { GlucoseRegulationInsights } from '../components/bloodworkz/glucose-regulation-insights';
import { MooPanel, MooPanelSlice, packMooColumn } from '../utils/pdfMooPagination';
import Storage from '@aws-amplify/storage';

const logo = require('../assets/imgs/logo.png');
const html2pdf = require('html2pdf.js');

Storage.configure({
  AWSS3: {
    bucket: 'labsmarts-next',
    region: 'us-east-1',
  },
});

// Shared style for the full-width page headers ("Results Overview", "Body Mass
// Index (BMI)", "Markers Outside Optimal"). They are all <h4> and so already
// resolve to Bootstrap's 1.5rem, but Results Overview and BMI now share a page
// — pinning the size here means the two titles can't drift apart if the h4
// cascade ever changes underneath them.
const PDF_PAGE_TITLE_STYLE: React.CSSProperties = {
  fontSize: '1.5rem',
  lineHeight: 1.2,
};

interface BloodworkAnalysisPDFContainerProps {
  bloodworkEntryStore?: BloodworkEntryStore;
  furtherConsiderationsEntryStore?: FurtherConsiderationsEntryStore;
  userProfileUIStore?: UserProfileUIStore;
}

type State = {
  withNotes: boolean;
  bloodworkAnalysis: BloodworkAnalysis;
  previousBloodwork?: Bloodwork;
  furtherConsiderations: FurtherConsiderations;
  headerLogoDataUrl: string | null;
  footerBrandDataUrl: string | null;
  isPdfReady: boolean;
  preparedPdf: any | null;
  // The set of sections the user excluded for this export, written to
  // sessionStorage by the parent window. Empty means everything is included.
  excludedFromPdfSections: string[];
  showOnlySectionSignals: boolean;
  showOnlyImmuneSectionSignals: boolean;
  showImmuneAbsolutes: boolean;
  // When true, a comparison PDF also renders bars for markers present in the
  // previous test but not the current one (previous blood drop only). Mirrored
  // from the practitioner's showPreviousTestMarkersInComparison setting.
  showPreviousTestMarkersInComparison: boolean;
  // Per-export override for "Markers Outside Optimal" bar graphs, mirrored from
  // the on-screen section-header checkbox. `null` → no override, fall back to
  // the saved `markersOutsideOptimalDisplay` profile preference.
  showBarsInMooOverride: boolean | null;
};

@inject(
  'bloodworkEntryStore',
  'furtherConsiderationsEntryStore',
  'userProfileUIStore',
)
@observer
export class BloodworkAnalysisPDFContainer extends React.Component<
  BloodworkAnalysisPDFContainerProps,
  State
> {
  state: State = {
    withNotes: false,
    bloodworkAnalysis: new BloodworkAnalysis(),
    previousBloodwork: undefined,
    furtherConsiderations: new FurtherConsiderations(),
    headerLogoDataUrl: null,
    footerBrandDataUrl: null,
    isPdfReady: false,
    preparedPdf: null,
    excludedFromPdfSections: [],
    showOnlySectionSignals: true,
    showOnlyImmuneSectionSignals: true,
    showImmuneAbsolutes: true,
    showPreviousTestMarkersInComparison: true,
    showBarsInMooOverride: null,
  };

  // Kept on the instance (not state) so the Results Overview panel can show
  // trend arrows for sections that have prior-test data.
  private previousBloodworkAnalysis?: BloodworkAnalysis;

  // --- Helpers ---------------------------------------------------------------

  // Turn any URL into a data URL (so html2canvas can render it reliably)
  private toDataUrl = async (url: string): Promise<string> => {
    const res = await fetch(url, { credentials: 'omit' });
    const blob = await res.blob();
    return await new Promise<string>(resolve => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result as string);
      reader.readAsDataURL(blob);
    });
  };

  // Resolve the HEADER logo (custom if present; otherwise bundled default) to a data URL
  private resolveHeaderLogoDataUrl = async (): Promise<string> => {
    const raw = this.getLogoUrl() as any;
    const candidate = (raw?.default ?? raw) as string | undefined;

    if (candidate === logo) {
      return await this.toDataUrl(candidate);
    }

    // 1) If getLogoUrl() returns http(s), try directly
    if (candidate && /^https?:\/\//i.test(candidate)) {
      try {
        return await this.toDataUrl(candidate);
      } catch {
        /* fall through */
      }
    }

    // 2) Try S3: user-logos/<filename>
    try {
      const key = (candidate || '').toString().split('/').pop();
      if (key) {
        const s3Url = await Storage.get(`user-logos/${key}`);
        return await this.toDataUrl(s3Url as string);
      }
    } catch {
      /* fall through */
    }

    // 3) Fallback: bundled default
    const bundled =
      (require('../assets/imgs/logo.png') as any)?.default ||
      (require('../assets/imgs/logo.png') as any);
    return await this.toDataUrl(bundled);
  };

  // Resolve the FOOTER brand mark (usually the bundled logo) to a data URL
  private resolveFooterBrandDataUrl = async (): Promise<string> => {
    const brandUrl =
      (require('../assets/imgs/logo.png') as any)?.default ||
      (require('../assets/imgs/logo.png') as any);
    return await this.toDataUrl(brandUrl);
  };

  // Prepare logos and PDF data in advance
  private preparePdfAssets = async (): Promise<void> => {
    try {
      // Load logos in parallel
      const [headerLogoDataUrl, footerBrandDataUrl] = await Promise.all([
        this.resolveHeaderLogoDataUrl(),
        this.resolveFooterBrandDataUrl(),
      ]);

      // NOTE: we intentionally do NOT set isPdfReady here. Keeping it false
      // until preparePdf() actually finishes means the "Preparing your
      // report..." text stays up continuously until the Export button is truly
      // ready to show. Previously isPdfReady flipped true at this point (after
      // the logos loaded but before the PDF was generated), so the text
      // vanished and left a multi-second gap with no visible button while
      // html2pdf ran.
      this.setState({
        headerLogoDataUrl,
        footerBrandDataUrl,
      });

      // Update the logo in DOM immediately for better UX
      const logoElement = document.getElementById('logo') as HTMLImageElement;
      if (logoElement && headerLogoDataUrl) {
        logoElement.src = headerLogoDataUrl;
      }

      // Now prepare the actual PDF
      await this.preparePdf();
    } catch (error) {
      console.error('Error preparing PDF assets:', error);
      // Set ready to true anyway so export can still work with fallback
      this.setState({ isPdfReady: true });
      this.showOrHidePdfExportButton(true);
    }
  };

  // Prepare the actual PDF object in advance
  private preparePdf = async (): Promise<void> => {
    try {
      const { bloodworkAnalysis, headerLogoDataUrl, footerBrandDataUrl } =
        this.state;

      if (!bloodworkAnalysis.bloodwork) {
        console.error('Bloodwork analysis not ready');
        return;
      }

      const firstInitialOfFirstName =
        bloodworkAnalysis.bloodwork.client.firstName.charAt(0);
      const lastName = bloodworkAnalysis.bloodwork.client.lastName;
      const collectionDate = moment(
        bloodworkAnalysis.bloodwork.collectionDate,
      ).format('YYYY-MM-DD');
      const filename = `${lastName}_${firstInitialOfFirstName}-LabSmarts_Analysis_${collectionDate}.pdf`;

      const rootEl = document.getElementById('test-results-analysis-panel');
      const pdfExportButton = document.getElementById('pdf-export-button');
      const cardHeaders = document.getElementsByClassName('card-header');

      if (pdfExportButton) pdfExportButton.style.opacity = '0';

      if (cardHeaders) {
        for (let i = 0; i < cardHeaders.length; i++) {
          (cardHeaders[i] as HTMLElement).style.marginTop = '-1px';
        }
      }

      if (!rootEl) {
        console.error('PDF root element not found');
        return;
      }

      // Helper: detect 'PNG' or 'JPEG' from data URL
      const detectType = (dataUrl: string): 'PNG' | 'JPEG' => {
        return dataUrl.startsWith('data:image/jpeg') ||
          dataUrl.startsWith('data:image/jpg')
          ? 'JPEG'
          : 'PNG';
      };
      const footerImgType = footerBrandDataUrl
        ? detectType(footerBrandDataUrl)
        : 'PNG';

      const opt = {
        margin: [8, -2, 5, 0],
        filename,
        image: { type: 'jpeg', quality: 1 },
        jsPDF: { orientation: 'p' },
        // Keep-together here is a nicety, not a guarantee. html2pdf skips any
        // element taller than a page, and it measures against a page grid
        // (pageSize.inner.px.height) that drifts ~1px per page from the grid the
        // canvas is actually sliced on (floor(canvas.width * inner.ratio)) — so
        // twenty pages in it no longer agrees with reality. Anything that MUST
        // land on a page boundary is pre-paginated in JS and forced with an
        // explicit `html2pdf__page-break` instead: the section packer above,
        // pdfMooPagination.ts, pdfSupplementsPagination.ts.
        pagebreak: {
          mode: ['css', 'legacy'],
          avoid: ['.disclaimer-panel', '.pdf-keep-together-block', '.pdf-glucose-bar-graphs', '.pdf-bmi-block'],
        },
        html2canvas: {
          scale: 1,
          useCORS: true,
          allowTaint: false,
          scrollX: 0,
          scrollY: 0,
          onclone: (doc: Document) => {
            const img = doc.getElementById('logo') as HTMLImageElement | null;
            if (img && headerLogoDataUrl) img.src = headerLogoDataUrl;
          },
        },
      };

      // Generate PDF and add footer
      const preparedPdf = await html2pdf()
        .set(opt)
        .from(rootEl)
        .toPdf()
        .get('pdf')
        .then((pdf: any) => {
          // Only add footer if we have the brand data URL
          if (footerBrandDataUrl) {
            // Compute brand logo aspect ratio from the data URL
            const props = pdf.getImageProperties(footerBrandDataUrl);
            const naturalRatio = props ? props.width / props.height : 5.5; // fallback ratio

            const footerFontSize = 8;
            const logoHeight = 3.8; // tweak to taste
            const logoWidth = logoHeight * naturalRatio;

            const rightText = 'Powered by';
            const leftPadding = 8;
            const rightPadding = 8;
            const bottomPadding = 6;
            const gap = 2;

            const fullName = `${bloodworkAnalysis.bloodwork.client.firstName} ${bloodworkAnalysis.bloodwork.client.lastName}`;
            const formattedDate = moment(
              bloodworkAnalysis.bloodwork.collectionDate,
            ).format('MM/DD/YYYY');

            // Add Prepared By if available
            const { userProfileUIStore } = this.props;
            const preparedBy =
              userProfileUIStore?.currentUserProfile?.preparedBy;

            let leftBase = `Prepared for: ${fullName}  •  Collection Date: ${formattedDate}`;
            if (preparedBy) {
              leftBase += `  •  Prepared by: ${preparedBy}`;
            }

            const totalPages = pdf.internal.getNumberOfPages();
            for (let i = 1; i <= totalPages; i++) {
              pdf.setPage(i);
              const pageWidth = pdf.internal.pageSize.getWidth();
              const pageHeight = pdf.internal.pageSize.getHeight();
              const baselineY = pageHeight - bottomPadding;

              pdf.setFontSize(footerFontSize);

              // Right block width
              const rightTextWidth = pdf.getTextWidth(rightText);
              const rightBlockWidth = rightTextWidth + gap + logoWidth;
              const rightStartX = pageWidth - rightPadding - rightBlockWidth;

              // Fit left text to avoid overlap
              const maxLeftWidth = rightStartX - leftPadding - 4;
              const fit = (s: string) => {
                if (pdf.getTextWidth(s) <= maxLeftWidth) return s;
                let t = s;
                while (t.length > 4 && pdf.getTextWidth(t + '…') > maxLeftWidth)
                  t = t.slice(0, -1);
                return t + '…';
              };
              const leftText = fit(leftBase);

              // Draw left and right blocks
              pdf.text(leftText, leftPadding, baselineY);
              pdf.text(rightText, rightStartX, baselineY);
              pdf.addImage(
                footerBrandDataUrl,
                footerImgType,
                rightStartX + rightTextWidth + gap,
                baselineY - logoHeight + 0.8,
                logoWidth,
                logoHeight,
              );
            }
          }

          return pdf;
        });

      this.setState({
        preparedPdf,
        isPdfReady: true,
      });

      // Show button only when PDF is ready
      this.showOrHidePdfExportButton(true);
      if (pdfExportButton) pdfExportButton.style.opacity = '1';
    } catch (error) {
      console.error('Error preparing PDF:', error);
      // Set ready to true anyway so export can still work with fallback
      this.setState({ isPdfReady: true });
      this.showOrHidePdfExportButton(true);
    }
  };

  // --------------------------------------------------------------------------

  componentDidMount() {
    this.showOrHidePdfExportButton(false);

    const withNotes = sessionStorage.getItem('exportWithNotes') === 'true';
    const bloodworkJSON = sessionStorage.getItem('bloodwork');
    const previousBloodworkJSON = sessionStorage.getItem('previousBloodwork');
    const excludedFromPdfSectionsJSON = sessionStorage.getItem('excludedFromPdfSections');
    const excludedFromPdfSections: string[] = excludedFromPdfSectionsJSON
      ? JSON.parse(excludedFromPdfSectionsJSON)
      : [];
    const showOnlySectionSignalsRaw = sessionStorage.getItem('showOnlySectionSignals');
    // Default to true (matches UserProfile default) if the key is missing,
    // e.g. when this window is reopened independently of the export flow.
    const showOnlySectionSignals =
      showOnlySectionSignalsRaw === null ? true : showOnlySectionSignalsRaw === 'true';
    const showOnlyImmuneSectionSignalsRaw = sessionStorage.getItem('showOnlyImmuneSectionSignals');
    const showOnlyImmuneSectionSignals =
      showOnlyImmuneSectionSignalsRaw === null ? true : showOnlyImmuneSectionSignalsRaw === 'true';
    const showImmuneAbsolutesRaw = sessionStorage.getItem('showImmuneAbsolutes');
    const showImmuneAbsolutes =
      showImmuneAbsolutesRaw === null ? true : showImmuneAbsolutesRaw === 'true';
    const showPreviousTestMarkersRaw = sessionStorage.getItem('showPreviousTestMarkersInComparison');
    const showPreviousTestMarkersInComparison =
      showPreviousTestMarkersRaw === null ? true : showPreviousTestMarkersRaw === 'true';
    const showBarsInMooRaw = sessionStorage.getItem('showBarsInMoo');
    const showBarsInMooOverride: boolean | null =
      showBarsInMooRaw === null ? null : showBarsInMooRaw === 'true';
    sessionStorage.clear();

    // Ensure profile is loaded (for custom logo)
    const { userProfileUIStore } = this.props;
    if (userProfileUIStore && !userProfileUIStore.currentUserProfile) {
      userProfileUIStore.loadCurrentUserProfile();
    }

    this.props.bloodworkEntryStore
      ?.loadBloodworkRanges()
      .then(bloodworkRanges => {
        const bloodwork = new Bloodwork(JSON.parse(bloodworkJSON!));
        const previousBloodwork = previousBloodworkJSON
          ? new Bloodwork(JSON.parse(previousBloodworkJSON))
          : undefined;

        const bloodworkAnalysis = new BloodworkAnalysis({
          bloodwork,
          bloodworkRanges,
          bloodworkFields: PanelField.ALL_FIELDS,
        });
        bloodworkAnalysis.init().performAnalysis();

        if (previousBloodwork && previousBloodwork.collectionDate) {
          const previousBloodworkAnalysis = new BloodworkAnalysis({
            bloodwork: previousBloodwork,
            bloodworkRanges,
            bloodworkFields: PanelField.ALL_FIELDS,
          });
          previousBloodworkAnalysis.init();
          bloodworkAnalysis.preparePreviousPanelMarkers(
            previousBloodworkAnalysis,
          );
          this.previousBloodworkAnalysis = previousBloodworkAnalysis;
        } else {
          this.previousBloodworkAnalysis = undefined;
        }
        return { bloodworkAnalysis, previousBloodwork };
      })
      .then(({ bloodworkAnalysis, previousBloodwork }) => {
        return this.props
          .furtherConsiderationsEntryStore!.loadFurtherConsiderations()
          .then(furtherConsiderations => ({
            bloodworkAnalysis,
            previousBloodwork,
            furtherConsiderations,
          }));
      })
      .then(
        ({ bloodworkAnalysis, previousBloodwork, furtherConsiderations }) => {
          this.setState({
            withNotes,
            bloodworkAnalysis,
            previousBloodwork,
            furtherConsiderations,
            excludedFromPdfSections,
            showOnlySectionSignals,
            showOnlyImmuneSectionSignals,
            showImmuneAbsolutes,
            showPreviousTestMarkersInComparison,
            showBarsInMooOverride,
          });
        },
      );
  }

  componentDidUpdate(_prevProps: any, prevState: State) {
    if (
      !Object.values(prevState.bloodworkAnalysis).filter(Boolean).length &&
      Object.values(this.state.bloodworkAnalysis).filter(Boolean).length
    ) {
      setTimeout(() => this.preparePdfAssets(), 2000);
    }
  }

  componentWillUnmount() {
    sessionStorage.clear();
  }

  componentWillReceiveProps() {
    this.scrollTop();
  }

  scrollTop() {
    if (
      typeof window !== 'undefined' &&
      typeof window.scrollTo === 'function'
    ) {
      window.scrollTo(0, 0);
    }
  }

  showOrHidePdfExportButton(show: boolean, timeout: number = 0) {
    setTimeout(() => {
      const opacity = show ? '1' : '0';
      const pdfExportButton = document.getElementById('pdf-export-button');
      if (pdfExportButton) {
        pdfExportButton.style.opacity = opacity;
      }
    }, timeout);
  }

  getLogoUrl = (): string => {
    const { userProfileUIStore } = this.props;
    const userProfile = userProfileUIStore?.currentUserProfile;
    return (
      userProfile?.hasCustomLogo() ? userProfile.customLogoUrl : logo
    ) as any;
  };

  // Legacy helper still used elsewhere
  getImageAsDataUrl = async (url: string) => {
    const res = await fetch(url);
    const blob = await res.blob();
    return new Promise(resolve => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result as string);
      reader.readAsDataURL(blob);
    });
  };

  // ========= EXPORT =========
  export = async () => {
    const { preparedPdf, isPdfReady, bloodworkAnalysis } = this.state;
    const firstInitialOfFirstName =
      bloodworkAnalysis.bloodwork.client.firstName.charAt(0);
    const lastName = bloodworkAnalysis.bloodwork.client.lastName;
    const collectionDate = moment(
      bloodworkAnalysis.bloodwork.collectionDate,
    ).format('YYYY-MM-DD');
    const filename = `${lastName}_${firstInitialOfFirstName}-LabSmarts_Analysis_${collectionDate}.pdf`;

    if (preparedPdf && isPdfReady) {
      preparedPdf.save(filename);
    } else {
      // Fallback: prepare PDF on the fly (shouldn't happen normally)
      console.log('PDF not prepared, falling back to on-demand generation...');
      await this.preparePdf();
      if (this.state.preparedPdf) {
        this.state.preparedPdf.save(filename);
      }
    }
  };

  pageBreakClassName(panelMarkers: PanelMarker[]): string {
    return panelMarkers.length > 0 ? 'html2pdf__page-break' : '';
  }

  render() {
    const { bloodworkAnalysis, furtherConsiderations, isPdfReady, excludedFromPdfSections, showOnlySectionSignals, showOnlyImmuneSectionSignals, showImmuneAbsolutes, showPreviousTestMarkersInComparison } = this.state;
    if (!bloodworkAnalysis.bloodwork) {
      return null;
    }
    // "Excluded from PDF" is the new per-export gate. UI collapse no longer
    // affects what ends up in the PDF; the "Include in PDF" checkbox in each
    // section header is the source of truth. Aliased as isCollapsed to keep
    // the call sites below unchanged.
    const isCollapsed = (section: Section) => excludedFromPdfSections.includes(section);
    const today = new Date().toISOString();

    // Only show Immune System Analysis if WBC has a value
    const wbc = (bloodworkAnalysis as any).wbc;
    const hasWbcValue = wbc?.hasValue?.() === true;

    // When comparing to a previous test, optionally append "previous-only"
    // markers (present last time, absent now) so their previous blood drop
    // still gets a bar graph — mirrors the on-screen Test Results Analysis
    // behavior. Gated on the practitioner's setting and on a previous test
    // being present. Sections with no current markers are left untouched (a
    // fully-empty section doesn't render). The combined arrays flow through
    // both the page-packing height estimates and the rendered charts so
    // pagination accounts for the extra rows.
    const showPreviousOnly =
      (showPreviousTestMarkersInComparison ?? true) && !!this.previousBloodworkAnalysis;
    const withPreviousOnly = (
      currentMarkers: PanelMarker[],
      getPrev: (a: BloodworkAnalysis) => PanelMarker[],
    ): PanelMarker[] => {
      if (!showPreviousOnly || currentMarkers.length === 0) {
        return currentMarkers;
      }
      const previousOnly = bloodworkAnalysis.getPreviousOnlyPanelMarkers(
        currentMarkers,
        getPrev(this.previousBloodworkAnalysis!),
      );
      return previousOnly.length
        ? [...currentMarkers, ...previousOnly]
        : currentMarkers;
    };

    // Combined per-section marker lists (current + previous-only) used for both
    // packing heights and the rendered bar graphs below. allMarkerSet (further
    // down) intentionally keeps the raw current-only getters, since previous-
    // only markers are blank in the current test and never belong in the
    // Markers Outside Optimal severity groups.
    const rbcMarkers = withPreviousOnly(bloodworkAnalysis.getRBCPanelMarkers(), a => a.getRBCPanelMarkers());
    const ironMarkers = withPreviousOnly(bloodworkAnalysis.getIronPanelMarkers(), a => a.getIronPanelMarkers());
    const lipidsMarkers = withPreviousOnly(bloodworkAnalysis.getLipidsPanelMarkers(), a => a.getLipidsPanelMarkers());
    const thyroidMarkers = withPreviousOnly(bloodworkAnalysis.getThyroidPanelMarkers(), a => a.getThyroidPanelMarkers());
    const proteinsMarkers = withPreviousOnly(bloodworkAnalysis.getProteinsPanelMarkers(), a => a.getProteinsPanelMarkers());
    const plateletMarkers = withPreviousOnly(bloodworkAnalysis.getPlateletPanelMarkers(), a => a.getPlateletPanelMarkers());
    const liverMarkers = withPreviousOnly(bloodworkAnalysis.getLiverPanelMarkers(), a => a.getLiverPanelMarkers());
    const kidneyMarkers = withPreviousOnly(bloodworkAnalysis.getKidneyPanelMarkers(), a => a.getKidneyPanelMarkers());
    const electrolyteMarkers = withPreviousOnly(bloodworkAnalysis.getElectrolytePanelMarkers(), a => a.getElectrolytePanelMarkers());
    const stressAdrenalMarkers = withPreviousOnly(bloodworkAnalysis.getStressAdrenalPanelMarkers(), a => a.getStressAdrenalPanelMarkers());
    const prostateMarkers = withPreviousOnly(bloodworkAnalysis.getProstatePanelMarkers(), a => a.getProstatePanelMarkers());

    // Clean leading blank lines/spaces from the global Notes block
    const notesObj = bloodworkAnalysis.bloodwork.bloodworkNotes.notes.find(
      n => n.section === Section.NOTES,
    );
    const globalNotesText = (notesObj?.text ?? '').trimStart();

    // Split Hormones section if there are more than 14 markers.
    // Previous-only markers are appended after the current ones (via
    // withPreviousOnly) before slicing, so the current markers always fill the
    // first page first.
    const sexHormoneMarkers = withPreviousOnly(bloodworkAnalysis.getSexHormonePanelMarkers(), a => a.getSexHormonePanelMarkers());
    const hormonesFirstPageMarkers = sexHormoneMarkers.slice(0, 13);
    const hormonesSecondPageMarkers = sexHormoneMarkers.slice(13);

    // Split Nutrients section if there are more than 14 markers
    const mineralMarkers = withPreviousOnly(bloodworkAnalysis.getMineralPanelMarkers(), a => a.getMineralPanelMarkers());
    const nutrientsFirstPageMarkers = mineralMarkers.slice(0, 13);
    const nutrientsSecondPageMarkers = mineralMarkers.slice(13);

    // Collect ALL markers from every section for the summary views.
    // We use a Set to deduplicate by object identity (same PanelMarker instance
    // may appear in multiple sections, e.g. globulin appears in WBC and Proteins).
    const allMarkerSet = new Set<PanelMarker>([
      ...bloodworkAnalysis.getRBCPanelMarkers(),
      ...bloodworkAnalysis.getIronPanelMarkers(),
      ...bloodworkAnalysis.getWBCPanelMarkers(),
      ...bloodworkAnalysis.getPlateletPanelMarkers(),
      ...bloodworkAnalysis.getGlucosePanelMarkers(),
      ...bloodworkAnalysis.getKidneyPanelMarkers(),
      ...bloodworkAnalysis.getProteinsPanelMarkers(),
      ...bloodworkAnalysis.getMineralPanelMarkers(),
      ...bloodworkAnalysis.getElectrolytePanelMarkers(),
      ...bloodworkAnalysis.getLiverPanelMarkers(),
      ...bloodworkAnalysis.getLipidsPanelMarkers(),
      ...bloodworkAnalysis.getInflammationOxidativeStressPanelMarkers(),
      ...bloodworkAnalysis.getThyroidPanelMarkers(),
      ...bloodworkAnalysis.getStressAdrenalPanelMarkers(),
      ...bloodworkAnalysis.getSexHormonePanelMarkers(),
      ...bloodworkAnalysis.getProstatePanelMarkers(),
    ]);
    const allMarkers = Array.from(allMarkerSet);

    // "Higher is better" markers (eGFR, PSA Free, PSA % Free) have no upper alarm bound
    // and should never appear in Above Optimal.
    const isHigherIsBetter = (pm: PanelMarker) =>
      pm.isEGFR?.() || pm.field?.useEGFRChart;

    // RDW-chart markers render everything below optimalMax as green — low values are fine,
    // so they must never appear in Below Optimal.
    const isRdwChart = (pm: PanelMarker) => pm.field?.useRDWChart;

    // For "higher is better" markers, alarmMin/labMin can be 0 (clamped from a negative
    // computed value), making isLessThanAlarm/isLessThanLab unreliable. Compare directly
    // against the stored range values and only use a threshold when it is > 0.
    const egfrBelowTier = (pm: PanelMarker): 1 | 2 | 3 => {
      const r = pm.range;
      if (!r) return 3;
      if (r.alarmMin > 0 && pm.value < r.alarmMin) return 1;
      if (r.labMin > 0 && pm.value < r.labMin) return 2;
      return 3;
    };

    // Zone-penetration score: fraction of the relevant danger zone consumed.
    // WBC uses its own 9-zone percentFromMean which is already zone-normalized.
    // Falls back to |percentFromMean| when denominators collapse to zero.
    const zonePenetrationScore = (pm: PanelMarker, section: 'alarm' | 'lab' | 'optimal'): number => {
      const r = pm.range;
      if (!r) return Math.abs(pm.percentFromMean ?? 0);
      if (pm.field?.useWBCPercentFromMean) return Math.abs(pm.percentFromMean ?? 0);
      const v = pm.value;
      if (section === 'alarm') {
        const denom = r.stdDev && r.stdDev > 0 ? r.stdDev : 1;
        if (v > r.alarmMax) return (v - r.alarmMax) / denom;
        if (r.alarmMin > 0 && v < r.alarmMin) return (r.alarmMin - v) / denom;
        return 0;
      }
      if (section === 'lab') {
        if (v > r.labMax) {
          const denom = r.alarmMax - r.labMax;
          return denom > 0 ? (v - r.labMax) / denom : Math.abs(pm.percentFromMean ?? 0);
        }
        const denom = r.labMin - (r.alarmMin ?? 0);
        return denom > 0 ? (r.labMin - v) / denom : Math.abs(pm.percentFromMean ?? 0);
      }
      // section === 'optimal'
      if (v > (r.optimalMax ?? r.labMax)) {
        const denom = r.labMax - (r.optimalMax ?? r.labMax);
        return denom > 0 ? (v - r.optimalMax!) / denom : Math.abs(pm.percentFromMean ?? 0);
      }
      const optMin = r.optimalMin ?? r.labMin;
      const denomOpt = optMin - r.labMin;
      return denomOpt > 0 ? (optMin - v) / denomOpt : Math.abs(pm.percentFromMean ?? 0);
    };

    const alarmZoneDesc   = (a: PanelMarker, b: PanelMarker) => zonePenetrationScore(b, 'alarm')   - zonePenetrationScore(a, 'alarm');
    const labZoneDesc     = (a: PanelMarker, b: PanelMarker) => zonePenetrationScore(b, 'lab')     - zonePenetrationScore(a, 'lab');
    const optimalZoneDesc = (a: PanelMarker, b: PanelMarker) => zonePenetrationScore(b, 'optimal') - zonePenetrationScore(a, 'optimal');

    // "Alarm Range Markers" — outside alarm threshold in either direction, worst first
    const needsAttentionGroup = allMarkers
      .filter(pm => pm.hasValue() && (
        isHigherIsBetter(pm)
          ? pm.isStricklyLessThanOptimal?.() && egfrBelowTier(pm) === 1
          : (pm.isGreaterThanAlarm?.() || (!isRdwChart(pm) && pm.isLessThanAlarm?.()))
      ))
      .sort(alarmZoneDesc);

    // "Outside Lab Range Markers" — outside lab range but within alarm, worst first
    const worthDiscussingGroup = allMarkers
      .filter(pm => pm.hasValue() && (
        isHigherIsBetter(pm)
          ? pm.isStricklyLessThanOptimal?.() && egfrBelowTier(pm) === 2
          : (
              (pm.isGreaterThanLab?.() && !pm.isGreaterThanAlarm?.()) ||
              (!isRdwChart(pm) && pm.isLessThanLab?.() && !pm.isLessThanAlarm?.())
            )
      ))
      .sort(labZoneDesc);

    // "Outside Optimal Range Markers" — outside optimal but within lab range, worst first
    const fineTuneGroup = allMarkers
      .filter(pm => pm.hasValue() && (
        isHigherIsBetter(pm)
          ? pm.isStricklyLessThanOptimal?.() && egfrBelowTier(pm) === 3
          : (
              (pm.isGreaterThanOptimal?.() && !pm.isGreaterThanLab?.()) ||
              (!isRdwChart(pm) && pm.isStricklyLessThanOptimal?.() && !pm.isLessThanLab?.())
            )
      ))
      .sort(optimalZoneDesc);

    // Split each severity group into high vs low for the 6-panel PDF layout
    // that mirrors the UI's Markers Outside Optimal section. eGFR ("higher is
    // better") markers only flag when low, so they always land in the low half.
    const splitByDirection = (markers: PanelMarker[], side: 'alarm' | 'lab' | 'optimal') => {
      const high: PanelMarker[] = [];
      const low: PanelMarker[] = [];
      for (const pm of markers) {
        if (isHigherIsBetter(pm)) { low.push(pm); continue; }
        const isHigh =
          side === 'alarm'   ? pm.isGreaterThanAlarm?.() :
          side === 'lab'     ? pm.isGreaterThanLab?.() :
                               pm.isGreaterThanOptimal?.();
        (isHigh ? high : low).push(pm);
      }
      return { high, low };
    };
    const { high: highAlarmGroup,    low: lowAlarmGroup }    = splitByDirection(needsAttentionGroup,  'alarm');
    const { high: aboveLabGroup,     low: belowLabGroup }    = splitByDirection(worthDiscussingGroup, 'lab');
    const { high: aboveOptimalGroup, low: belowOptimalGroup } = splitByDirection(fineTuneGroup,       'optimal');

    // Whether the Markers Outside Optimal section renders at all — same three
    // gates the MOO JSX below applies (per-export "Include in PDF", the saved
    // "Hide section" display preference, and having any out-of-optimal marker).
    // Hoisted here because MOO now sits between the last bar-graph section and
    // the trailing notes block, so the trailing-notes pagination math has to
    // know whether it does.
    const mooShown =
      !isCollapsed(Section.MARKERS_OUTSIDE_OPTIMAL) &&
      this.props.userProfileUIStore?.currentUserProfile?.markersOutsideOptimalDisplay !== 'hideSection' &&
      (needsAttentionGroup.length > 0 ||
        worthDiscussingGroup.length > 0 ||
        fineTuneGroup.length > 0);

    // Greedy bin-packing: group sections onto pages to minimise blank space.
    // Each unit's height is estimated from marker count; units that fit together
    // share a page (no break between them). The last unit never gets a break-after
    // so that Insights can flow onto the same page if room remains.
    //
    // Tuning guide — increase PAGE_CAPACITY to pack more sections per page;
    // decrease it to be more conservative:
    //
    //   Constant      | Value  | Meaning
    //   --------------|--------|--------------------------------------------------
    //   PAGE_CAPACITY | 900 px | Usable vertical space per page
    //   BASE_HEIGHT   | 180 px | Fixed overhead per section (title + table header)
    //   ROW_HEIGHT    |  35 px | Height per bar-graph marker row
    //   SECTION_GAP   |  24 px | Spacing between sections sharing a page
    const PAGE_CAPACITY = 900;
    const BASE_HEIGHT   = 180;
    const ROW_HEIGHT    = 40;
    const SECTION_GAP   = 24;

    // Per-section notes no longer render under each section's bar graphs — they
    // are consolidated into the Section Notes panel near the report bottom (see
    // SectionNotesSummaryPanel / the trailing-notes block below). So a section's
    // height is just its bar graphs.
    const estH = (markers: PanelMarker[]) =>
      BASE_HEIGHT + markers.length * ROW_HEIGHT;

    const glucoseDisplayMarkers = withPreviousOnly(
      (() => {
        const all = bloodworkAnalysis.getGlucosePanelMarkers();
        return isHdlHighSoleOutOfOptimal(all)
          ? all.filter(pm => pm.attribute !== 'hdl')
          : all;
      })(),
      a => a.getGlucosePanelMarkers(),
    );

    const inflammationDisplayMarkers = withPreviousOnly(
      (() => {
        const all = bloodworkAnalysis.getInflammationOxidativeStressPanelMarkers();
        return showOnlySectionSignals
          ? all.filter(keepInInflammationPanel)
          : all;
      })(),
      a => a.getInflammationOxidativeStressPanelMarkers(),
    );

    const immuneDisplayMarkers = withPreviousOnly(
      (() => {
        const all = bloodworkAnalysis.getWBCPanelMarkers();
        const base = showImmuneAbsolutes ? all : all.filter(pm => !isImmuneAbsolute(pm));
        return showOnlyImmuneSectionSignals
          ? suppressAlpWhenZincSupersedes(base.filter(pm => !isGlobulinOptimal(pm)).filter(keepInImmunePanel), all)
          : base;
      })(),
      a => a.getWBCPanelMarkers(),
    );

    // Glucose prints its Insights gauge panel ABOVE its bar graphs (see
    // PDFSection.evaluation), so the page footprint of the glucose unit is
    // panel + gap + bars, not bars alone. Leaving the panel out of the estimate
    // made the packer think the next section (typically Vitamin & Mineral
    // Status) still fitted on the glucose page: its title and panel header
    // printed at the bottom of that page and its marker rows spilled onto the
    // next one. Zero when the panel doesn't render, in which case the section
    // really is just its bar graphs.
    const glucoseInsightsHeight =
      GlucoseRegulationInsights.estimatedPdfHeight(bloodworkAnalysis);
    const glucoseUnitHeight =
      estH(glucoseDisplayMarkers) +
      (glucoseInsightsHeight > 0 ? glucoseInsightsHeight + SECTION_GAP : 0);

    const packUnits = [
      { id: 'IRON',            height: estH(ironMarkers),                                                          visible: !isCollapsed(Section.IRON) && ironMarkers.length > 0 },
      { id: 'GLUCOSE',         height: glucoseUnitHeight,                                                       visible: !isCollapsed(Section.GLUCOSE) && glucoseDisplayMarkers.length > 0 },
      { id: 'LIPIDS',          height: estH(lipidsMarkers),                                                      visible: !isCollapsed(Section.LIPIDS) && lipidsMarkers.length > 0 },
      { id: 'THYROID',         height: estH(thyroidMarkers),                                                    visible: !isCollapsed(Section.THYROID) && thyroidMarkers.length > 0 },
      // For split sections, count notes on the first part only (notes appear after the first chunk).
      { id: 'NUTRIENTS_1',     height: estH(nutrientsFirstPageMarkers),                                       visible: !isCollapsed(Section.NUTRIENTS) && nutrientsFirstPageMarkers.length > 0 },
      { id: 'NUTRIENTS_2',     height: estH(nutrientsSecondPageMarkers),                                                         visible: !isCollapsed(Section.NUTRIENTS) && nutrientsSecondPageMarkers.length > 0 },
      { id: 'PROTEINS',        height: estH(proteinsMarkers),                                                  visible: !isCollapsed(Section.PROTEINS) && proteinsMarkers.length > 0 },
      { id: 'INFLAMMATION',    height: estH(inflammationDisplayMarkers),                  visible: !isCollapsed(Section.INFLAMMATION_OXIDATIVE_STRESS) && inflammationDisplayMarkers.length > 0 },
      { id: 'PLATELETS',       height: estH(plateletMarkers),                                                 visible: !isCollapsed(Section.PLATELETS) && plateletMarkers.length > 0 },
      { id: 'LIVER',           height: estH(liverMarkers),                                                visible: !isCollapsed(Section.LIVER_BILIARY) && liverMarkers.length > 0 },
      { id: 'KIDNEY',          height: estH(kidneyMarkers),                                                      visible: !isCollapsed(Section.KIDNEY) && kidneyMarkers.length > 0 },
      { id: 'ELECTROLYTES',    height: estH(electrolyteMarkers),                                           visible: !isCollapsed(Section.ELECTROLYTES) && electrolyteMarkers.length > 0 },
      { id: 'STRESS_ADRENAL',  height: estH(stressAdrenalMarkers),                                       visible: !isCollapsed(Section.STRESS_ADRENAL) && stressAdrenalMarkers.length > 0 },
      { id: 'SEX_HORMONE_1',   height: estH(hormonesFirstPageMarkers),                                      visible: !isCollapsed(Section.SEX_HORMONE) && hormonesFirstPageMarkers.length > 0 },
      { id: 'SEX_HORMONE_2',   height: estH(hormonesSecondPageMarkers),                                                          visible: !isCollapsed(Section.SEX_HORMONE) && hormonesSecondPageMarkers.length > 0 },
      { id: 'PROSTATE',        height: estH(prostateMarkers),                                                  visible: !isCollapsed(Section.PROSTATE) && prostateMarkers.length > 0 },
    ];

    // When WBC is visible it always occupies its own page between PROTEINS
    // and INFLAMMATION. The first visible unit AFTER PROTEINS (typically
    // INFLAMMATION) starts fresh on a new page after WBC's, so the packer
    // resets pageUsed at that point instead of running a single packing pass
    // across the WBC boundary. Iron / Glucose / Lipids each have their own
    // forced page break in the JSX (empty html2pdf__page-break markers);
    // listing them in forcedPageStartIds tells the packer to reset pageUsed
    // when it reaches each one so subsequent sections pack correctly.
    const wbcIntervenes = hasWbcValue && !isCollapsed(Section.WBC);
    const { unitBreakAfter, pageUsed: packPageUsed } = computePdfSectionPacking({
      packUnits,
      wbcIntervenes,
      preWbcBarrierId: 'PROTEINS',
      forcedPageStartIds: ['IRON', 'GLUCOSE', 'LIPIDS'],
      pageCapacity: PAGE_CAPACITY,
      sectionGap: SECTION_GAP,
    });
    let pageUsed = packPageUsed;

    // Force-break helpers. We intentionally avoid empty `html2pdf__page-break`
    // marker divs (which create blank intermediate pages when the preceding
    // section happens to exactly fill its page) and instead set break-after
    // on the LAST visible preceding pack unit. The
    // `html2pdf__page-break` class is then applied to that unit's wrapper via
    // sectionClass(), which html2pdf-legacy treats as "break after this
    // element" — producing exactly one break between the two sections.
    const findLastVisiblePackUnitBefore = (id: string): string | null => {
      const idx = packUnits.findIndex(u => u.id === id);
      if (idx <= 0) return null;
      for (let i = idx - 1; i >= 0; i--) {
        if (packUnits[i].visible) return packUnits[i].id;
      }
      return null;
    };

    // GLUCOSE / LIPIDS both start on a fresh page via empty break markers
    // in the JSX (not via break-after on their wrappers, because those
    // sections have keep-together inside). We must explicitly suppress
    // any break-after the packer would have set on the section just before
    // each of them, otherwise we get two consecutive page breaks and a
    // blank page between them.
    const glucoseVisible = !isCollapsed(Section.GLUCOSE) && glucoseDisplayMarkers.length > 0;
    if (glucoseVisible) {
      const before = findLastVisiblePackUnitBefore('GLUCOSE');
      if (before) unitBreakAfter[before] = false;
    }
    const lipidsVisible = !isCollapsed(Section.LIPIDS) && lipidsMarkers.length > 0;
    if (lipidsVisible) {
      const before = findLastVisiblePackUnitBefore('LIPIDS');
      if (before) unitBreakAfter[before] = false;
    }

    // When WBC intervenes it always starts on a fresh page. Force break-after
    // on the last visible pre-WBC pack unit (typically PROTEINS).
    if (wbcIntervenes) {
      const lastPreWbc = findLastVisiblePackUnitBefore('INFLAMMATION');
      if (lastPreWbc) unitBreakAfter[lastPreWbc] = true;
    }
    // Trailing "notes" block below the last bar-graph section: the consolidated
    // Section Notes panel (each section's notes, gathered) followed by the
    // global Insights note. Both flow after the last section and share its page
    // when they fit; when they don't, the whole block is pushed to a fresh page
    // (html2pdf then flows any overflow onto subsequent pages). Both are gated
    // by the "Export with notes" master toggle; Section Notes also honours its
    // own "Include in PDF" checkbox.
    const sectionNotesIncluded =
      this.state.withNotes && !isCollapsed(Section.SECTION_NOTES);
    const sectionNotesText = sectionNotesIncluded
      ? SectionNotesSummaryPanel.bodyText(bloodworkAnalysis).trim()
      : '';
    const sectionNotesShown = !!sectionNotesText;
    const sectionNotesEstimatedHeight = sectionNotesShown
      ? SectionNotesSummaryPanel.estimatedPdfHeight(bloodworkAnalysis)
      : 0;

    // Insights card estimate: 200px card overhead + 24px/line, counting a
    // `## ` line at heading height (it renders as an h6 via renderNoteBody, not
    // as body text).
    const insightsLines = this.state.withNotes && globalNotesText
      ? globalNotesText.split('\n')
      : [];
    const insightsEstimatedHeight = insightsLines.length > 0
      ? 200 +
        insightsLines.reduce(
          (h, line) => h + (line.trim().startsWith('## ') ? 44 : 24),
          0,
        )
      : 0;

    // If the combined trailing block won't fully fit in the remaining space on
    // the last section's page, force it to a fresh page. Estimate conservatively
    // (+50px buffer) so when we say it fits, html2pdf won't split a card header off.
    //
    // When MOO renders it sits between the last bar-graph section and this
    // block, and its first chunk carries `pageBreakBefore: 'always'` — so the
    // last section's page is already ended and `pageUsed` no longer describes
    // the page the notes would land on (that page holds MOO's last chunk, whose
    // height the column packer owns at render time). Rather than thread that
    // height back here, the notes simply always start on their own page after
    // MOO, via the break marker in the JSX. Forcing a break-after on the last
    // pack unit as well would put two breaks in a row and emit a blank page.
    const trailingNotesHeight = sectionNotesEstimatedHeight + insightsEstimatedHeight;
    const TRAILING_NOTES_SAFETY_BUFFER = 50;
    const trailingNotesNeedsOwnPage =
      !mooShown &&
      trailingNotesHeight > 0 &&
      trailingNotesHeight > PAGE_CAPACITY - pageUsed - TRAILING_NOTES_SAFETY_BUFFER;

    // The trailing block reaches its own page via `page-break-before` on its own
    // wrapper (see the JSX below) rather than a break-after on the last section.
    // Both mechanisms are the same padding trick underneath — html2pdf's
    // pagebreak plugin pads `pageHeight - (top % pageHeight)` before the element
    // (break-before) or after it (break-after / the legacy marker class) — but
    // break-before measures the block we care about, and, crucially, it carries
    // the block's own padding-top with it.
    //
    // That padding is what fixes the reported clipping: the plugin's page grid
    // drifts ~1px per page from the grid the canvas is actually sliced on, so
    // content the plugin parks exactly on a page boundary is sliced a few px
    // higher, printing that sliver at the bottom of the previous page. With no
    // buffer that sliver was the top edge of the Section Notes card; with the
    // pad it is blank padding. Padding, not margin — a top margin can be
    // collapsed at the boundary — which is the same technique
    // .pdf-section-title uses to protect section titles.
    // One value for all three trailing cards (Section Notes, Insights,
    // Disclaimer) so the white space above them reads the same wherever each
    // one lands. Measured on a real 18-page export, the gaps were 23 / 18 / 45
    // px: three different pads, plus the Insights card's 48px bottom margin
    // spilling onto the Disclaimer's page. The margins are gone (see the cards
    // below) and each card's space above is now just this pad, less the few px
    // html2pdf loses to its page grid — about 12px on the page, roughly half
    // what it was. Each card's pad must live INSIDE the element that gets
    // moved to a fresh page, since that is the box html2pdf relocates; a pad
    // on an outer wrapper would stay behind on the previous page.
    const CARD_TOP_PAD = 18;
    const trailingNotesShown =
      sectionNotesShown || !!(this.state.withNotes && globalNotesText);
    // After MOO the block always starts fresh — see the comment above for why
    // the fit math can't describe the MOO page.
    const trailingNotesOwnPage =
      trailingNotesShown && (mooShown || trailingNotesNeedsOwnPage);

    // Glucose's bar graphs sit inside a keep-together wrapper
    // (.pdf-glucose-bar-graphs), and a break-after class on a wrapper with
    // page-break-inside: avoid inside it fires unreliably — the same root cause
    // that made Lipid Balance use an empty break marker after Glucose instead.
    // So whenever the packer decides the following section can't share Glucose's
    // page, we hand that break to the marker in the JSX below and clear the
    // class, keeping exactly one break between them. (Lipids already emits that
    // marker unconditionally, and the packer suppresses the break-after before
    // it, so this only ever applies when Lipids is hidden.)
    const glucoseBreakAfterMarker = !!unitBreakAfter['GLUCOSE'] && !lipidsVisible;
    if (glucoseBreakAfterMarker) {
      unitBreakAfter['GLUCOSE'] = false;
    }

    // The Disclaimer no longer computes an own-page decision from estimates.
    // It is a short, fixed card, so `.pdf-keep-together-block`
    // (page-break-inside: avoid, see pdf.css) lets html2pdf decide from the REAL
    // geometry at export time: it prints in whatever room is left, or moves
    // whole to the next page when it would split. That removes the blank page a
    // forced break produced here — html2pdf implements every forced break as a
    // padding div of `pageHeight - (top % pageHeight)`, so a break that fires
    // when the flow has just crossed a boundary (e.g. the Insights card's 48px
    // bottom margin spilling over) pads out an entire otherwise-empty page. The
    // comparison report reached the same conclusion for its own disclaimer.
    const enabledSupplementBrands =
      this.props.userProfileUIStore?.currentUserProfile?.enabledSupplementBrands;
    const holobiomeIncluded =
      supplementsVisible() &&
      !isCollapsed(HOLOBIOME_SECTION) &&
      isSystemicFormulasEnabled(enabledSupplementBrands);
    // The roadmap prints in two parts: the kit boxes + Add-On Products (which
    // paginate themselves — see HolobiomeRoadmapPdf), then the
    // Pattern-Identified Supplements table on a fresh page after them. That
    // table comes out empty when nothing Systemic Formulas was recommended, in
    // which case the kits are the last thing the roadmap prints.
    const holobiomeEstimatedHeight = holobiomeIncluded
      ? SupplementsSummaryPanel.estimatedPdfHeight(
          bloodworkAnalysis,
          furtherConsiderations,
          enabledSupplementBrands,
          'systemicFormulas',
        )
      : 0;
    const holobiomePatternTableIncluded =
      holobiomeIncluded && holobiomeEstimatedHeight > 0;
    // Same content check for the roadmap itself — its block opens with a break
    // and spacer too, and the estimator returns 0 exactly when the roadmap has
    // no pages to print.
    const holobiomeRoadmapIncluded =
      holobiomeIncluded &&
      estimatedHolobiomeRoadmapPdfHeight(
        bloodworkAnalysis,
        furtherConsiderations,
        enabledSupplementBrands,
      ) > 0;
    // Same guard the Holobiome pattern table carries: the Supplements block
    // opens with a page break and a 28px spacer, so rendering it when the panel
    // has nothing to show (no non-Systemic-Formulas products for this test)
    // costs a forced page break plus 28px of white above whatever follows — it
    // was pushing the Disclaimer onto its own page and padding it out to 39px
    // against the notes cards' 12px.
    const supplementsIncluded =
      supplementsVisible() && !isCollapsed(Section.SUPPLEMENTS);
    const supplementsPanelIncluded =
      supplementsIncluded &&
      SupplementsSummaryPanel.estimatedPdfHeight(
        bloodworkAnalysis,
        furtherConsiderations,
        enabledSupplementBrands,
        'otherBrands',
      ) > 0;

    // WBC always starts on its own page. This is now handled by the
    // unitBreakAfter override above (which sets break-after on the last
    // visible pre-WBC pack unit), rather than by an empty break div in JSX —
    // using a single mechanism avoids the blank-page issue that arose when
    // the previous section exactly filled its page.

    // `pdf-section-block` (display: flow-root, see pdf.css) is on every section
    // wrapper, break or no break: it stops the results Card's bottom margin
    // escaping the wrapper, which is what made a break-after land the NEXT
    // section 10px lower on its page than a marker-reached section. Mid-page
    // spacing is unchanged — the margin still occupies the same 10px, it just
    // counts inside the wrapper now.
    const sectionClass = (id: string) =>
      unitBreakAfter[id] ? 'pdf-section-block html2pdf__page-break' : 'pdf-section-block';

    // Whether a section prints below the one before it rather than at the top
    // of a fresh page. The inter-section spacers below are gated on this: a
    // spacer that survives a page break does NOT stay behind at the bottom of
    // the previous page (html2pdf's break-after inserts its padding right after
    // the previous section's wrapper, and the spacer comes after that) — it
    // rides to the top of the new page and pushes the title down. Measured on a
    // real export: Liver Function and Electrolyte Balance opened 37px below the
    // page edge against ~17px for every other packer-break section.
    const sharesPageWithPrevious = (id: string) => {
      const before = findLastVisiblePackUnitBefore(id);
      return !!before && !unitBreakAfter[before];
    };

    // Build attribute → body-system label map for grouping markers in the
    // dashboard sections. Shared with the web view via mooSystemGrouping so the
    // two never drift apart. Markers group by primary home; cross-cutting
    // relevance (Inflammation / Glucose) is tagged per-marker.
    const mooSystemDefs = buildSystemGroupDefs(bloodworkAnalysis);
    const attrToSystem = buildAttrToSystem(mooSystemDefs);
    const secondaryTags = buildSecondaryTagger(mooSystemDefs);

    const groupBySystem = (
      markers: PanelMarker[],
      scoreOf: (pm: PanelMarker) => number,
    ) => appendSecondaryLabels(groupMarkersBySystem(markers, scoreOf, attrToSystem), secondaryTags);

    return (
      <>
        {/* Status row lives OUTSIDE #test-results-analysis-panel (the element
            html2pdf captures) so the "Preparing your report..." text / Export
            button is never baked into the generated PDF. */}
        <div className="text-right" style={{ marginTop: 12, paddingRight: 10 }} id="pdf-status-row">
          {isPdfReady ? (
            <Button
              id="pdf-export-button"
              onClick={this.export}
              disabled={!isPdfReady}
              style={{ opacity: isPdfReady ? 1 : 0 }}
            >
              {isPdfReady ? 'Export to PDF' : 'Preparing PDF...'}
            </Button>
          ) : (
            <span
              style={{
                color: '#766d66',
                fontStyle: 'italic',
                fontSize: 15,
              }}
            >
              Preparing your report... the Export to PDF button will appear
              here in a few seconds.
            </span>
          )}
        </div>
      <div className="pdf-page" id="test-results-analysis-panel">

        {/* Header */}
        <div className="text-center" style={{ marginTop: 24 }}>
          <Image
            id="logo"
            src={this.getLogoUrl()}
            alt="Current Logo"
            fluid
            style={{
              maxHeight: '100px',
              maxWidth: '400px',
              objectFit: 'contain',
            }}
          />
        </div>
        <div style={{ marginTop: 16 }}>
          <h3 className="text-center">
            <strong>Blood Work Analysis Report</strong>
          </h3>
          <div style={{ height: 12 }} aria-hidden />
          <div style={{ height: 48 }} aria-hidden />
        </div>

        {/* First-page meta. No break-after — the Results Overview block that
            follows and the first per-system section (RBC) carry their own
            `pageBreakBefore: 'always'`. Using break-BEFORE on each
            block instead of break-AFTER on the prior one avoids the html2pdf
            phantom-blank-page bug: a chain of `page-break-after: always`
            siblings would emit an extra blank page after the last one in the
            chain (the blank page that kept appearing between the last cover
            block and RBC). */}
        <div style={{ marginTop: 48 }}>
          <div>
            <h5>
              <strong>Prepared for</strong>
            </h5>
            <h5>
              {bloodworkAnalysis.bloodwork.client.firstName}{' '}
              {bloodworkAnalysis.bloodwork.client.lastName}
            </h5>
            <div style={{ height: 12 }} aria-hidden />
            {this.props.userProfileUIStore?.currentUserProfile?.preparedBy && (
              <>
                <h5>
                  <strong>Prepared by</strong>
                </h5>
                <h5>
                  {this.props.userProfileUIStore.currentUserProfile.preparedBy}
                </h5>
                <div style={{ height: 12 }} aria-hidden />
              </>
            )}
            <h5>
              <strong>Collection Date</strong>
            </h5>
            <h5>
              <Moment format="MM/DD/YYYY">
                {bloodworkAnalysis.bloodwork.collectionDate}
              </Moment>
            </h5>
            <div style={{ height: 12 }} aria-hidden />
            {this.state.previousBloodwork && (
              <>
                <h5>
                  <strong>Previous Collection Date</strong>
                </h5>
                <h5>
                  <Moment format="MM/DD/YYYY">
                    {this.state.previousBloodwork.collectionDate}
                  </Moment>
                </h5>
                <div style={{ height: 12 }} aria-hidden />
              </>
            )}
            <h5>
              <strong>Analysis Date</strong>
            </h5>
            <h5>
              <Moment format="MM/DD/YYYY">{today}</Moment>
            </h5>
          </div>

          {/* Results Overview — page 2, started by its own
              `pageBreakBefore: 'always'` rather than the cover-meta block's
              break-after. See the cover-meta comment above for why we use
              break-BEFORE on each block instead of break-AFTER on the prior. */}
          {(() => {
            // Hide the Results Overview when there are no blood markers to
            // summarize (e.g. a test where only a burden score was entered in
            // another tab) — mirrors the on-screen panel's own hiding rule.
            const resultsOverviewIncluded = !isCollapsed(Section.RESULTS_OVERVIEW)
              && hasResultsOverviewContent(
                bloodworkAnalysis,
                this.previousBloodworkAnalysis,
                hasWbcValue,
                showOnlySectionSignals,
                showOnlyImmuneSectionSignals,
              );
            const bmiIncluded = !isCollapsed(Section.BMI);
            if (!resultsOverviewIncluded && !bmiIncluded) return null;

            const client = bloodworkAnalysis.bloodwork.client;
            const isPregnant = client.gender === Gender.FEMALE && client.menopause === Menopause.PREGNANT;
            const bw = bloodworkAnalysis.bloodwork as any;
            const heightCm = client.height != null ? Number(client.height) : (bw.height != null ? Number(bw.height) : null);
            const weightLbs = client.weight != null ? Number(client.weight) : (bw.weight != null ? Number(bw.weight) : null);
            const bmiNumber = heightCm != null && weightLbs != null
              ? MathUtils.bmi(weightLbs, heightCm)
              : null;
            const bmiSex: 'M' | 'F' | undefined = client.gender === Gender.MALE
              ? 'M'
              : client.gender === Gender.FEMALE ? 'F' : undefined;
            const bmiAgeYears = client.ageAsOf(bloodworkAnalysis.bloodwork.collectionDate);
            const canRenderBmi = bmiIncluded
              && bmiNumber != null
              && !isPregnant
              && !(bmiAgeYears != null && bmiAgeYears < 2);

            if (!resultsOverviewIncluded && !canRenderBmi) return null;

            return (
              <div style={{ pageBreakBefore: 'always' }}>
                {resultsOverviewIncluded && (
                  <>
                    <h4 style={{ ...PDF_PAGE_TITLE_STYLE, marginBottom: 32 }}><strong>Results Overview</strong></h4>
                    <ResultsOverviewPanel
                      bloodworkAnalysis={bloodworkAnalysis}
                      previousBloodworkAnalysis={this.previousBloodworkAnalysis}
                      hasWbcValue={hasWbcValue}
                      direction="column"
                      showOnlySectionSignals={showOnlySectionSignals}
                      showOnlyImmuneSectionSignals={showOnlyImmuneSectionSignals}
                      showImmuneAbsolutes={showImmuneAbsolutes}
                    />
                  </>
                )}
                {canRenderBmi && (
                  <div className="pdf-bmi-block" style={{ marginTop: resultsOverviewIncluded ? 32 : 0 }}>
                    {/* One line-space of breathing room above the title (only
                        when the Results Overview panel sits above it). Held
                        deliberately tight: the two blocks share a page, and the
                        old 80 + 48 gap was enough on its own to push BMI onto a
                        page of its own. `.pdf-bmi-block` keeps the title and
                        chart together if a tall overview ever does bump it. */}
                    <h4 style={{ ...PDF_PAGE_TITLE_STYLE, marginTop: 0, marginBottom: 12 }}>
                      <strong>Body Mass Index (BMI)</strong>
                    </h4>
                    <div style={{ display: 'flex', justifyContent: 'flex-start' }}>
                      <BMIBarChart
                        bmi={bmiNumber!}
                        ageYears={bmiAgeYears}
                        sex={bmiSex}
                      />
                    </div>
                  </div>
                )}
              </div>
            );
          })()}

          {/* Per-system sections. RBC starts on a fresh page via an empty
              `html2pdf__page-break` marker (legacy mode) rather than an inline
              `pageBreakBefore` (CSS mode). With consecutive siblings each
              carrying `pageBreakBefore: 'always'` (Results Overview, RBC),
              html2pdf was emitting a phantom blank page after the last cover
              block. The marker pattern matches what IRON/GLUCOSE/LIPIDS use
              below and avoids the conflict. The title's own padding-top
              (.pdf-section-title) buffers it from the page edge so html2pdf
              doesn't clip the top of the text. */}
          {!isCollapsed(Section.RBC) && rbcMarkers.length > 0 && (
            <>
              <div className="html2pdf__page-break" />
              <div className="pdf-section-block">
                <PDFSection
                  bloodworkAnalysis={bloodworkAnalysis}
                  furtherConsiderations={furtherConsiderations}
                  title="Blood Oxygen & Red Blood Cell Function"
                  section={Section.RBC}
                  panelMarkers={rbcMarkers}
                />
              </div>
            </>
          )}
          {/* Empty break marker AFTER RBC ensures Iron Status starts at the
              top of a fresh page. We use a marker (rather than a break-after
              class on RBC's wrapper) because RBC's bar-graphs wrapper inside
              has page-break-inside: avoid; the class on the outer wrapper
              fires unreliably in that case, leaving Iron partway down the
              prior page. Iron's title relies on its own padding-top
              (.pdf-section-title) for the page-edge buffer. Only fires when
              BOTH RBC and IRON have markers — otherwise the marker would emit
              a blank page (PDFSection renders nothing when markers.length ===
              0, but the break marker above it would still fire). */}
          {!isCollapsed(Section.RBC) && rbcMarkers.length > 0
            && !isCollapsed(Section.IRON) && ironMarkers.length > 0 && (
            <>
              <div className="html2pdf__page-break" />
            </>
          )}

          {!isCollapsed(Section.IRON) && (
            <div className={sectionClass('IRON')}>
              <PDFSection
                bloodworkAnalysis={bloodworkAnalysis}
                furtherConsiderations={furtherConsiderations}
                title="Iron Status & Utilization"
                section={Section.IRON}
                panelMarkers={ironMarkers}
              />
            </div>
          )}

          {!isCollapsed(Section.GLUCOSE) && (
            <>
              {/* Glucose Regulation & Metabolic Function always starts on its
                  own page so its panel and bar graphs sit together below the
                  page header. The title's own padding-top (.pdf-section-title)
                  buffers it from the page edge so html2pdf doesn't clip the
                  top of the title text. */}
              <div className="html2pdf__page-break" />
              <div className={sectionClass('GLUCOSE')}>
                <PDFSection
                  bloodworkAnalysis={bloodworkAnalysis}
                  furtherConsiderations={furtherConsiderations}
                  title="Glucose Regulation & Metabolic Function"
                  section={Section.GLUCOSE}
                  panelMarkers={glucoseDisplayMarkers}
                />
              </div>
            </>
          )}
          {/* Empty break marker AFTER Glucose ensures Lipid Balance starts
              at the top of a fresh page. We use a marker rather than a
              break-after class on Glucose's wrapper because in non-prod
              Glucose has a keep-together wrapper around its bar graphs and
              the break-after class fires unreliably in that case (same root
              cause as the WBC/MOO/Iron issues). Lipid's title relies on its
              own padding-top (.pdf-section-title) for the page-edge buffer.

              The same marker carries the packer's break when Lipids is hidden
              and the next section (Thyroid / Vitamin & Mineral Status / …)
              doesn't fit under the Glucose Insights panel + bar graphs — see
              glucoseBreakAfterMarker above, which clears the unreliable
              break-after class so only one break fires here. */}
          {!isCollapsed(Section.GLUCOSE) && (lipidsVisible || glucoseBreakAfterMarker) && (
            <>
              <div className="html2pdf__page-break" />
            </>
          )}

          {!isCollapsed(Section.LIPIDS) && (
            <div className={sectionClass('LIPIDS')}>
              <PDFSection
                bloodworkAnalysis={bloodworkAnalysis}
                furtherConsiderations={furtherConsiderations}
                title="Lipid Balance"
                section={Section.LIPIDS}
                panelMarkers={lipidsMarkers}
              />
            </div>
          )}

          {!isCollapsed(Section.THYROID) && (
            <div className={sectionClass('THYROID')}>
              <PDFSection
                bloodworkAnalysis={bloodworkAnalysis}
                furtherConsiderations={furtherConsiderations}
                title="Thyroid Function"
                section={Section.THYROID}
                panelMarkers={thyroidMarkers}
              />
            </div>
          )}

          {/* Vitamin & Mineral Status (with automatic split after 13 markers) */}
          {mineralMarkers.length > 0 && !isCollapsed(Section.NUTRIENTS) && (
            <>
              <div className={sectionClass('NUTRIENTS_1')}>
                <PDFSection
                  bloodworkAnalysis={bloodworkAnalysis}
                  furtherConsiderations={furtherConsiderations}
                  title="Vitamin & Mineral Status"
                  section={Section.NUTRIENTS}
                  panelMarkers={nutrientsFirstPageMarkers}
                />
              </div>

              {nutrientsSecondPageMarkers.length > 0 && (
                <div className={sectionClass('NUTRIENTS_2')}>
                  <PDFSection
                    bloodworkAnalysis={bloodworkAnalysis}
                    furtherConsiderations={furtherConsiderations}
                    title="Vitamin & Mineral Status (cont.)"
                    section={Section.NUTRIENTS}
                    panelMarkers={nutrientsSecondPageMarkers}
                  />
                </div>
              )}
            </>
          )}

          {!isCollapsed(Section.PROTEINS) && (
            <div className={sectionClass('PROTEINS')}>
              <PDFSection
                bloodworkAnalysis={bloodworkAnalysis}
                furtherConsiderations={furtherConsiderations}
                title="Protein Status"
                section={Section.PROTEINS}
                panelMarkers={proteinsMarkers}
              />
            </div>
          )}

          {hasWbcValue && !isCollapsed(Section.WBC) && (
            // Immune Activity is forced to a fresh page by setting break-after
            // on the last visible pre-WBC pack unit (typically PROTEINS). See
            // the unitBreakAfter override above. To force the FOLLOWING
            // Inflammation section onto its own fresh page, we insert an
            // empty break marker AFTER WBC's content. (Putting the class on
            // WBC's outer wrapper interacts badly with the bar-graphs
            // keep-together avoid inside it, leaving the break in the wrong
            // position.) Markers placed AFTER content are safe — they have
            // zero height and sit at the end of the last page of preceding
            // content, then trigger the page-break for the next element.
            // Inflammation's title relies on its own padding-top
            // (.pdf-section-title) for the page-edge buffer.
            <>
              {/* Immune Activity reaches this fresh page via break-after on the
                  last pre-WBC pack unit (PROTEINS) — the same packer-break
                  mechanism Thyroid / Liver / Kidney / Hormones / Prostate use.
                  Those sections carry NO leading spacer and let the title's own
                  padding-top (.pdf-section-title, 10px) buffer the page edge,
                  and their titles sit flush at the top of the page. The Immune
                  gauge previously had an extra spacer here, which made its title
                  sit noticeably lower than every other section title. A spacer
                  at a page-break boundary is collapsed/absorbed by html2pdf
                  (see pdf.css), so resizing it had no visible effect — the title
                  was always positioned by the break + its own padding. Dropping
                  the spacer entirely lines the title up with the other packer-
                  break sections.

                  All immune markers render in one "Immune Activity Results"
                  panel (see PDFSection's refRangeTitle): the gauge sits on its
                  own page, then the single bar-graph panel — with its own title
                  — follows on the next page. */}
              <div className="pdf-section-block">
                <PDFSection
                  bloodworkAnalysis={bloodworkAnalysis}
                  furtherConsiderations={furtherConsiderations}
                  title="Immune Activity"
                  section={Section.WBC}
                  panelMarkers={immuneDisplayMarkers}
                />
              </div>
              <div className="html2pdf__page-break" />
            </>
          )}

          {!isCollapsed(Section.INFLAMMATION_OXIDATIVE_STRESS) && (
            // Inflammation gets its fresh page via the break-after class on
            // WBC's wrapper above (when WBC is visible). When WBC is hidden,
            // the packer decides whether Inflammation packs with the prior
            // section.
            <div className={sectionClass('INFLAMMATION')}>
              <PDFSection
                bloodworkAnalysis={bloodworkAnalysis}
                furtherConsiderations={furtherConsiderations}
                title="Inflammation & Oxidative Stress"
                section={Section.INFLAMMATION_OXIDATIVE_STRESS}
                panelMarkers={inflammationDisplayMarkers}
              />
            </div>
          )}

          {!isCollapsed(Section.PLATELETS) && (
            <div className={sectionClass('PLATELETS')}>
              <PDFSection
                bloodworkAnalysis={bloodworkAnalysis}
                furtherConsiderations={furtherConsiderations}
                title="Blood Clotting Function"
                section={Section.PLATELETS}
                panelMarkers={plateletMarkers}
              />
            </div>
          )}

          {/* Line-space spacer so Liver Function reads as visually separated
              from Blood Clotting Function when they share a page — and only
              then. It does NOT stay behind at the bottom of the previous page
              when the packer breaks before Liver; it rides to the top of the
              new page and pushes the title 20px lower than every other
              section's. See sharesPageWithPrevious. */}
          {!isCollapsed(Section.PLATELETS) && !isCollapsed(Section.LIVER_BILIARY) &&
            sharesPageWithPrevious('LIVER') && (
            <div style={{ height: 20 }} aria-hidden />
          )}

          {!isCollapsed(Section.LIVER_BILIARY) && (
            <div className={sectionClass('LIVER')}>
              <PDFSection
                bloodworkAnalysis={bloodworkAnalysis}
                furtherConsiderations={furtherConsiderations}
                title="Liver Function"
                section={Section.LIVER_BILIARY}
                panelMarkers={liverMarkers}
              />
            </div>
          )}

          {!isCollapsed(Section.KIDNEY) && (
            <div className={sectionClass('KIDNEY')}>
              <PDFSection
                bloodworkAnalysis={bloodworkAnalysis}
                furtherConsiderations={furtherConsiderations}
                title="Kidney Function"
                section={Section.KIDNEY}
                panelMarkers={kidneyMarkers}
              />
            </div>
          )}

          {/* Line-space spacer so Electrolyte Balance reads as visually
              separated from Kidney Function when they share a page — same
              gating as Liver's above. */}
          {!isCollapsed(Section.KIDNEY) && !isCollapsed(Section.ELECTROLYTES) &&
            sharesPageWithPrevious('ELECTROLYTES') && (
            <div style={{ height: 20 }} aria-hidden />
          )}

          {!isCollapsed(Section.ELECTROLYTES) && (
            <div className={sectionClass('ELECTROLYTES')}>
              <PDFSection
                bloodworkAnalysis={bloodworkAnalysis}
                furtherConsiderations={furtherConsiderations}
                title="Electrolyte Balance"
                section={Section.ELECTROLYTES}
                panelMarkers={electrolyteMarkers}
              />
            </div>
          )}

          {!isCollapsed(Section.STRESS_ADRENAL) && stressAdrenalMarkers.length > 0 && (
            <div className={sectionClass('STRESS_ADRENAL')}>
              <PDFSection
                bloodworkAnalysis={bloodworkAnalysis}
                furtherConsiderations={furtherConsiderations}
                title="Stress & Adrenal Balance"
                section={Section.STRESS_ADRENAL}
                panelMarkers={stressAdrenalMarkers}
              />
            </div>
          )}

          {/* Hormones Analysis (with automatic split after 13 markers) */}
          {sexHormoneMarkers.length > 0 && !isCollapsed(Section.SEX_HORMONE) && (
            <>
              <div className={sectionClass('SEX_HORMONE_1')}>
                <PDFSection
                  bloodworkAnalysis={bloodworkAnalysis}
                  furtherConsiderations={furtherConsiderations}
                  title="Hormone Balance"
                  section={Section.SEX_HORMONE}
                  panelMarkers={hormonesFirstPageMarkers}
                />
              </div>

              {hormonesSecondPageMarkers.length > 0 && (
                <div className={sectionClass('SEX_HORMONE_2')}>
                  <PDFSection
                    bloodworkAnalysis={bloodworkAnalysis}
                    furtherConsiderations={furtherConsiderations}
                    title="Hormone Balance (cont.)"
                    section={Section.SEX_HORMONE}
                    panelMarkers={hormonesSecondPageMarkers}
                  />
                </div>
              )}
            </>
          )}

          {!isCollapsed(Section.PROSTATE) && (
            <div className={sectionClass('PROSTATE')}>
              <PDFSection
                bloodworkAnalysis={bloodworkAnalysis}
                furtherConsiderations={furtherConsiderations}
                title="Prostate Function"
                section={Section.PROSTATE}
                panelMarkers={prostateMarkers}
              />
            </div>
          )}

          {/* Markers Outside Optimal — two stacked columns (highs left, lows
              right), pre-paginated by the column packer in
              src/utils/pdfMooPagination.ts. Tall content (e.g. 16 markers in
              one "Above Optimal Range" panel) is sliced between marker rows
              into multiple cards; the section header is repeated as
              "Markers Outside Optimal (cont.)" on each spillover page. Each
              page chunk carries `pageBreakBefore: 'always'` so the first
              chunk lands on a fresh page and each subsequent chunk lands on
              its own page.

              Placed after the last per-system section rather than up front
              with the Results Overview, matching the on-screen report order
              (bloodwork-test-analysis.tsx): it's a long reference list, not a
              lead-in. `mooShown` gates it — the same flag the trailing-notes
              pagination above reads, so the two can't drift apart. The notes
              block below carries its own break marker, so we don't need a
              break-after on the last chunk. */}
          {mooShown && (() => {
              // Mirror the UI's bar-chart view in the PDF. The on-screen
              // "Show bar graphs" checkbox is the per-export source of truth
              // (passed via sessionStorage); when missing (e.g. PDF window
              // reopened directly), fall back to the saved
              // `markersOutsideOptimalDisplay` profile preference. Only
              // 'hideBars' drops the bars — 'minimizeSection' is a screen-only
              // collapse, so a minimized section still exports with them.
              const moodPref = this.props.userProfileUIStore?.currentUserProfile?.markersOutsideOptimalDisplay ?? 'showBars';
              const showBars = this.state.showBarsInMooOverride !== null
                ? this.state.showBarsInMooOverride
                : moodPref !== 'hideBars';
              // Zone colors mirror the UI marker badges — pastel tints kept in
              // sync with zoneColors in bloodwork-test-analysis.tsx and the
              // .summary-marker-badge rules in pdf.css.
              const zoneColors = (s: Section): { bg: string; fg: string } => {
                if (s === Section.NEEDS_ATTENTION)  return { bg: '#F3B7AC', fg: '#7a2e20' };
                if (s === Section.WORTH_DISCUSSING) return { bg: '#F7D6A9', fg: '#6b4400' };
                return { bg: '#F1E7A9', fg: '#5a5200' };
              };

              type PanelDef = {
                key: string;
                title: string;
                sectionForZone: Section;
                markers: PanelMarker[];
                scoreOf: (pm: PanelMarker) => number;
              };

              const panelDefs: PanelDef[] = [
                { key: 'highAlarm',    title: 'High Alarm Range',    sectionForZone: Section.NEEDS_ATTENTION,  markers: highAlarmGroup,    scoreOf: pm => zonePenetrationScore(pm, 'alarm') },
                { key: 'aboveLab',     title: 'Above Lab Range',     sectionForZone: Section.WORTH_DISCUSSING, markers: aboveLabGroup,     scoreOf: pm => zonePenetrationScore(pm, 'lab') },
                { key: 'aboveOptimal', title: 'Above Optimal Range', sectionForZone: Section.FINE_TUNE,        markers: aboveOptimalGroup, scoreOf: pm => zonePenetrationScore(pm, 'optimal') },
                { key: 'lowAlarm',     title: 'Low Alarm Range',     sectionForZone: Section.NEEDS_ATTENTION,  markers: lowAlarmGroup,     scoreOf: pm => zonePenetrationScore(pm, 'alarm') },
                { key: 'belowLab',     title: 'Below Lab Range',     sectionForZone: Section.WORTH_DISCUSSING, markers: belowLabGroup,     scoreOf: pm => zonePenetrationScore(pm, 'lab') },
                { key: 'belowOptimal', title: 'Below Optimal Range', sectionForZone: Section.FINE_TUNE,        markers: belowOptimalGroup, scoreOf: pm => zonePenetrationScore(pm, 'optimal') },
              ];

              // Pagination constants — letter portrait, 96dpi-ish. Tuned with
              // safety margin so estimates round up (we'd rather leave whitespace
              // at a page bottom than overflow). If MOO consistently leaves too
              // much whitespace, raise PAGE_CAPACITY; if cards still get clipped,
              // raise the per-element overheads.
              const PAGE_CAPACITY  = 900;          // usable height after section h4 (raised from 880: a single 80px bar + 32px system header overshot by only 8-16px, spilling lone markers like RDW/MCHC onto an extra page)
              const CARD_OVERHEAD  = 96;           // .card-header + .card-body padding + borders
              const GROUP_OVERHEAD = 32;           // .summary-system-header + group margin
              const MARKER_H       = showBars ? 80 : 32;
              const COLUMN_GAP     = 8;

              const toMooPanel = (def: PanelDef): MooPanel => ({
                key: def.key,
                title: def.title,
                sectionForZone: def.sectionForZone,
                groups: groupBySystem(def.markers, def.scoreOf),
              });

              const leftPanels  = [panelDefs[0], panelDefs[1], panelDefs[2]]
                .filter(p => p.markers.length > 0).map(toMooPanel);
              const rightPanels = [panelDefs[3], panelDefs[4], panelDefs[5]]
                .filter(p => p.markers.length > 0).map(toMooPanel);

              const packArgs = {
                pageCapacity: PAGE_CAPACITY,
                cardOverhead: CARD_OVERHEAD,
                groupOverhead: GROUP_OVERHEAD,
                markerHeight: MARKER_H,
                columnGap: COLUMN_GAP,
              };
              const leftPages  = packMooColumn({ panels: leftPanels,  ...packArgs });
              const rightPages = packMooColumn({ panels: rightPanels, ...packArgs });
              const totalChunks = Math.max(leftPages.length, rightPages.length);

              const renderSlice = (slice: MooPanelSlice) => {
                const colors = zoneColors(slice.sectionForZone);
                // Show the range's TOTAL marker count, even when the panel
                // is split across pages — not the per-page subset count.
                const sliceMarkerCount = slice.totalMarkerCount;
                return (
                  <Card
                    key={slice.key}
                    className="summary-section-card"
                    style={{ borderColor: colors.bg }}
                  >
                    <Card.Header
                      style={{
                        backgroundImage: 'none',
                        backgroundColor: colors.bg,
                        color: colors.fg,
                        borderBottomColor: colors.bg,
                      }}
                    >
                      {slice.title}&nbsp;({sliceMarkerCount})
                    </Card.Header>
                    <Card.Body>
                      <SummaryDashboardSection
                        title=""
                        section={slice.sectionForZone}
                        groups={slice.groups}
                        showCharts={showBars}
                        chartsBelow={true}
                      />
                    </Card.Body>
                  </Card>
                );
              };

              const renderColumn = (slices: MooPanelSlice[]) => (
                <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  {slices.map(s => renderSlice(s))}
                </div>
              );

              return Array.from({ length: totalChunks }).map((_, i) => {
                const left  = leftPages[i]  ?? [];
                const right = rightPages[i] ?? [];
                if (left.length === 0 && right.length === 0) return null;
                return (
                  <div key={`moo-chunk-${i}`} style={{ pageBreakBefore: 'always' }}>
                    {/* Top buffer so html2pdf doesn't clip the page header when
                        the chunk lands on a fresh page (break-before). This h4
                        is a plain page header (no .pdf-section-title), so it
                        relies on this spacer rather than the title padding the
                        per-section titles use. */}
                    <div style={{ height: 16 }} aria-hidden />
                    {/* Constrain the MOO section to the PDF page content width
                        (matches the per-section bar graphs' .ref-range-analysis-panel,
                        which is a fixed 760px). The mini bar-graph charts are
                        Highcharts SVGs sized to their container width AT MOUNT.
                        On screen this preview panel is full browser width, but
                        html2pdf clones it into a container forced to the ~801px
                        page width — so without a fixed width the columns are wide
                        in the preview and narrow in the clone. Highcharts' SVG
                        keeps its wide preview pixel width, overflows the narrow
                        clone column, and the right side of each chart (red + half
                        the orange band) is clipped. Pinning the section to 760px
                        makes the columns identical in the preview and the clone,
                        so the charts render at the right width everywhere and the
                        drop markers stay aligned. */}
                    <div style={{ maxWidth: 760, margin: '0 auto' }}>
                      {/* 24px (not 12) because the .pdf-page > div > h4 rule
                          in pdf.css that adds the extra header gap no longer
                          matches this h4 now that it's nested inside the
                          fixed-width wrapper — keep the spacing inline. */}
                      <h4 style={{ marginBottom: 24 }}>
                        <strong>Markers Outside Optimal{i > 0 ? ' (cont.)' : ''}</strong>
                      </h4>
                      <div style={{ display: 'flex', gap: '8px', alignItems: 'flex-start' }}>
                        {renderColumn(left)}
                        {renderColumn(right)}
                      </div>
                    </div>
                  </div>
                );
              });
            })()}

          {/* Trailing notes block — the consolidated Section Notes panel above
              the global Insights note. Either card can be absent (Section Notes
              has its own "Include in PDF" checkbox; the Insights note only
              prints when the practitioner wrote one), so page-edge protection
              is applied per card rather than assuming which one leads:

                - the OUTER wrapper owns the block's page break —
                  `page-break-before: always` when it needs a fresh page (always
                  after MOO, otherwise when it won't fit in what's left of the
                  last bar-graph page), with padding-top buffering whichever
                  card comes first. See the trailingNotesOwnPage comment above
                  for why this replaced the break-after mechanism. When the
                  block shares the last section's page, a 12px pad separates the
                  two instead.
                - the Insights card carries its OWN keep-together wrapper with
                  its own padding-top, because wherever it lands it can be moved
                  by that rule — after the Section Notes card, or on its own
                  when the block was estimated to fit the last section's page
                  and didn't. html2pdf parks a moved element flush on its page
                  grid, which sits above the grid the canvas is sliced on, so
                  without that padding the top of its blue header printed as a
                  sliver on the previous page. The pad travels inside the moved
                  element, so it is the only buffer that survives the move — the
                  outer wrapper's stays behind on the previous page.

              The outer pad is therefore dropped when the Insights card is the
              only card in the block (its own pad already buffers the block's
              top) and kept when Section Notes leads, which has no keep-together
              of its own: that card can run past a page, and html2pdf skips the
              rule for anything over one page tall, so it paginates naturally by
              design. */}
          {trailingNotesShown && (
            <div
              style={{
                // The Insights card brings its own pad (below), so the outer
                // one is only needed when Section Notes leads the block.
                paddingTop: sectionNotesShown ? CARD_TOP_PAD : 0,
                ...(trailingNotesOwnPage ? { pageBreakBefore: 'always' } : {}),
              }}
            >
              {sectionNotesShown && (
                <SectionNotesSummaryPanel
                  bloodworkAnalysis={bloodworkAnalysis}
                  forPdf
                />
              )}

              {this.state.withNotes && globalNotesText && (
                <div
                  className="pdf-keep-together-block"
                  style={{ paddingTop: CARD_TOP_PAD }}
                >
                  {/* No bottom margin: a trailing margin here spilled across
                      the page boundary and stacked on top of the Disclaimer's
                      own pad, which is why its gap measured 45px against this
                      card's 18px. Spacing above a card is owned by that card's
                      pad, not by the margin of whatever precedes it. */}
                  <Card
                    className="disclaimer-panel"
                    style={{ whiteSpace: 'pre-wrap' }}
                  >
                    <Card.Header className="py-1">Insights</Card.Header>
                    <Card.Body
                      className="disclaimer-body note-card-body"
                      style={{ whiteSpace: 'pre-wrap' }}
                    >
                      {/* Same renderer as the on-screen Insights preview, so a
                          `## Heading` the practitioner typed reads as a heading
                          here too instead of printing its own markup. pre-WRAP
                          (not pre-line) so hand-added spacing survives into the
                          PDF. */}
                      {renderNoteBody(globalNotesText)}
                    </Card.Body>
                  </Card>
                </div>
              )}
            </div>
          )}

          {/* Holobiome Roadmap — the whole Systemic Formulas section, in the
              order the screen shows it: the five monthly kit boxes, the Add-On
              Products table, then the Pattern-Identified Supplements table.
              Gated on the roadmap's own "Include in PDF" checkbox and on the
              Systemic Formulas brand, so unchecking that brand takes the
              section out of the PDF exactly as it does on screen.

              Each block starts on a fresh page. The break/spacer here opens the
              first one; HolobiomeRoadmapPdf forces its own between the kit
              pages and the add-on table. Skipped when the roadmap has no pages,
              for the same reason the two blocks below are. */}
          {holobiomeRoadmapIncluded && (
            <>
              <div className="html2pdf__page-break" />
              <div style={{ height: 28 }} aria-hidden />
              <HolobiomeRoadmapPdf
                bloodworkAnalysis={bloodworkAnalysis}
                furtherConsiderations={furtherConsiderations}
                enabledSupplementBrands={enabledSupplementBrands}
              />
            </>
          )}

          {/* The roadmap's third block. Skipped entirely when this test has no
              Systemic Formulas recommendations — the panel would render null,
              and the break above it would push the disclaimer onto a page of
              its own for nothing. */}
          {holobiomePatternTableIncluded && (
            <>
              <div className="html2pdf__page-break" />
              <div style={{ height: 28 }} aria-hidden />
              <SupplementsSummaryPanel
                bloodworkAnalysis={bloodworkAnalysis}
                furtherConsiderations={furtherConsiderations}
                enabledSupplementBrands={enabledSupplementBrands}
                brandScope="systemicFormulas"
                title={PDF_PATTERN_TABLE_TITLE}
                forPdf
              />
            </>
          )}

          {/* Supplements summary — collects each section's Supplements tab
              into one panel styled like the Insights card. Every brand EXCEPT
              Systemic Formulas, which the Holobiome Roadmap card above owns.
              Gated on the "Include in PDF" checkbox (default-on via the user's
              Settings) and only rendered outside prod, matching the
              per-section Supplements tab visibility. Always starts on a fresh
              page — and is skipped entirely when the panel would come out
              empty, so that break and spacer don't strand the Disclaimer. */}
          {supplementsPanelIncluded && (
            <>
              <div className="html2pdf__page-break" />
              {/* Push the panel down from the page edge so the header isn't
                  hugged tight to the top margin or clipped by the page break.
                  Trimmed by one line height per user request. */}
              <div style={{ height: 28 }} aria-hidden />
              {/* No keep-together wrapper: the panel pre-paginates itself into
                  one card per page and forces its own breaks (see
                  pdfSupplementsPagination.ts), so wrapping the whole run would
                  be both a no-op and a lie about its shape. */}
              <SupplementsSummaryPanel
                bloodworkAnalysis={bloodworkAnalysis}
                furtherConsiderations={furtherConsiderations}
                enabledSupplementBrands={enabledSupplementBrands}
                brandScope="otherBrands"
                forPdf
              />
            </>
          )}

          {/* Disclaimer — short and fixed, so it keeps itself together instead
              of being forced onto its own page by an estimate: the wrapper's
              page-break-inside: avoid moves it whole to the next page when it
              would split, measured from real geometry at export time, and
              leaves it in place when it fits. The wrapper (not the card) owns
              the rule so its padding-top rides along and buffers the panel from
              the page edge on the spillover path. */}
          <div className="pdf-keep-together-block" style={{ paddingTop: CARD_TOP_PAD }}>
            <DisclaimerSection furtherConsiderations={furtherConsiderations} />
          </div>
        </div>
      </div>
      </>
    );
  }
}
